import { useEffect, useRef, useState } from "react";

import { SiteFooter } from "@/components/SiteFooter";
import { SiteHeader } from "@/components/SiteHeader";
import { LP_CHROME_CSS } from "@/lib/lp-chrome-css";
import { PILES_GURGAON_CSS, PILES_GURGAON_HTML } from "@/lib/piles-gurgaon-lp";
import { notifyForm } from "@/lib/notifyForm";

function istHour(): number {
  try {
    return parseInt(
      new Date().toLocaleString("en-US", {
        timeZone: "Asia/Kolkata",
        hour: "2-digit",
        hour12: false,
      }),
      10,
    );
  } catch {
    return 12;
  }
}

interface PilesSurgeryCostGurgaonPageProps {
  pageType?: "cost" | "treatment";
  city?: "Gurgaon" | "Muzaffarnagar";
}

const PANKAJ_IMG = "/__l5e/assets-v1/6ba148c0-7277-4284-9909-5f89d092d637/dr-pankaj-sharma.jpg";

/** Muzaffarnagar version of the approved Gurgaon cost page, featuring Dr. Pankaj Sharma. */
export function toMuzaffarnagarHtml(html: string): string {
  return html
    .replaceAll("/__l5e/assets-v1/24a6ff4e-783c-4c4e-9264-94e1e7fd469f/dr-narendra-kishor-mandal.jpeg", PANKAJ_IMG)
    .replaceAll("Dr. Narendra Kishor Mandal", "Dr. Pankaj Sharma")
    .replaceAll("DNB (Urology), M.S. (General Surgery), MBBS", "MBBS, MS (General Surgery), FICS, FMAS, FIAGES")
    .replaceAll(
      "https://maps.google.com/maps?q=Healic+Multispecialty+Hospital+Badshahpur+Gurugram&ll=28.3862333,77.0512987&z=16&output=embed",
      "https://maps.google.com/maps?q=Healic+Hospital+37%2F10+Circular+Road+Civil+Lines+Muzaffarnagar&z=15&output=embed",
    )
    .replace(
      "Tower-B, F-141, Highway, Sector 68<br>Badshahpur Sohna Road, Gurgaon, Haryana 122101",
      "37/10 Circular Road, Santosh Vihar<br>Civil Lines South, Muzaffarnagar, UP 251001",
    )
    .replace("Near: Sector 68, Badshahpur Sohna Road", "Near: Circular Road, Civil Lines South")
    .replaceAll("Healic Multispecialty Hospital, Gurgaon", "Healic Hospital, Muzaffarnagar")
    .replaceAll("Healic Multispeciality Hospital, Gurgaon", "Healic Hospital, Muzaffarnagar")
    .replaceAll("https://healic.in/gurgaon/piles-surgery-cost", "https://healic.in/Muzaffarnagar/piles-surgery-cost")
    .replaceAll("Gurugram", "Muzaffarnagar")
    .replaceAll("Gurgaon", "Muzaffarnagar");
}

export function PilesSurgeryCostGurgaonPage({ pageType = "cost", city = "Gurgaon" }: PilesSurgeryCostGurgaonPageProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [ctaMode, setCtaMode] = useState<"active" | "off">("active");
  const pageHtml =
    pageType === "treatment"
      ? PILES_GURGAON_HTML.replace(
          "Piles Surgery in Gurgaon · Fixed-Price Package · Cashless Insurance",
          "Advanced Piles Treatment in Gurgaon · Laser · Stapler · Surgery",
        )
          .replace(
            "Piles Surgery <span class=\"pop\">Cost</span> in Gurgaon",
            "Piles <span class=\"pop\">Treatment</span> in Gurgaon",
          )
          .replace(
            "An all-inclusive fixed-price package for piles — laser, stapler or open surgery, assessed and treated by Dr. Narendra Kishor Mandal and the general surgery team at Healic Gurgaon. Cashless insurance &amp; 0% EMI.",
            "Get expert diagnosis and personalised piles treatment with laser, stapler or conventional surgery from Dr. Narendra Kishor Mandal and the general surgery team at Healic Gurgaon. Daycare care, cashless insurance and transparent packages available.",
          )
          .replace("Get My Package Cost", "Book Piles Consultation")
          .replace("Piles surgery cost in Gurgaon", "Piles treatment in Gurgaon")
          .replace("piles surgery cost in Gurgaon", "piles treatment cost in Gurgaon")
          .replace("Piles surgery cost in Gurgaon?", "Which piles treatment is right for me in Gurgaon?")
      : PILES_GURGAON_HTML;
  const finalHtml = city === "Muzaffarnagar" ? toMuzaffarnagarHtml(pageHtml) : pageHtml;

  useEffect(() => {
    const hour = istHour();
    setCtaMode(hour >= 9 && hour < 20 ? "active" : "off");
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const cleanups: Array<() => void> = [];
    const leadModal = root.querySelector<HTMLElement>("#lead-modal");
    const callModal = root.querySelector<HTMLElement>("#call-modal");
    const formContainer = root.querySelector<HTMLElement>("#modal-form-container");
    const thankYou = root.querySelector<HTMLElement>("#thank-you");

    const openModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.add("open");
      modal.setAttribute("aria-hidden", "false");
      document.documentElement.classList.add("modal-open");
      const dialog = modal.querySelector<HTMLElement>(".modal-dialog");
      if (dialog) dialog.scrollTop = 0;
    };
    const closeModal = (modal: HTMLElement | null) => {
      if (!modal) return;
      modal.classList.remove("open");
      modal.setAttribute("aria-hidden", "true");
      if (!root.querySelector(".modal-backdrop.open")) {
        document.documentElement.classList.remove("modal-open");
      }
    };
    const openLeadForm = (doctor = "") => {
      const doctorField = root.querySelector<HTMLInputElement>("#m-doctor");
      const pageField = root.querySelector<HTMLInputElement>("#m-pageurl");
      if (doctorField) doctorField.value = doctor;
      if (pageField) pageField.value = window.location.href;
      if (formContainer) formContainer.style.display = "";
      thankYou?.classList.remove("active");
      openModal(leadModal);
      setTimeout(() => root.querySelector<HTMLInputElement>("#m-name")?.focus({ preventScroll: true }), 350);
    };

    const clickHandler = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      const closeButton = target?.closest("[data-modal-close]");
      if (closeButton) {
        event.preventDefault();
        closeModal(closeButton.closest(".modal-backdrop") as HTMLElement);
        return;
      }
      const opener = target?.closest("[data-open-form]");
      if (opener) {
        event.preventDefault();
        openLeadForm(opener.getAttribute("data-doctor") || "");
      }
    };
    root.addEventListener("click", clickHandler);
    cleanups.push(() => root.removeEventListener("click", clickHandler));

    root.querySelectorAll<HTMLElement>(".modal-backdrop").forEach((backdrop) => {
      const handler = (event: Event) => {
        if (event.target === backdrop) closeModal(backdrop);
      };
      backdrop.addEventListener("click", handler);
      cleanups.push(() => backdrop.removeEventListener("click", handler));
    });

    const keyHandler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        root.querySelectorAll<HTMLElement>(".modal-backdrop.open").forEach(closeModal);
      }
    };
    document.addEventListener("keydown", keyHandler);
    cleanups.push(() => document.removeEventListener("keydown", keyHandler));

    root.querySelectorAll<HTMLAnchorElement>('a[href^="tel:"]').forEach((link) => {
      const handler = (event: Event) => {
        if (root.dataset.ctaMode === "off") {
          event.preventDefault();
          openLeadForm();
          return;
        }
        if (window.matchMedia("(min-width: 900px) and (pointer: fine)").matches) {
          event.preventDefault();
          openModal(callModal);
        }
      };
      link.addEventListener("click", handler);
      cleanups.push(() => link.removeEventListener("click", handler));
    });

    const form = root.querySelector<HTMLFormElement>("#modal-form");
    if (form) {
      const handler = (event: Event) => {
        event.preventDefault();
        const data = new FormData(form);
        const name = String(data.get("name") ?? "").trim();
        void notifyForm({
          formType: pageType === "treatment" ? "Piles Treatment Consultation" : "Piles Surgery Cost Consultation",
          source: pageType === "treatment" ? `${city} Piles Treatment Page` : `${city} Piles Surgery Cost Page`,
          fields: [
            { label: "Name", value: name },
            { label: "Phone", value: String(data.get("phone") ?? "").trim() },
            { label: "Treatment", value: "Piles Surgery" },
            { label: "City", value: city },
            { label: "Doctor", value: String(data.get("doctor") ?? "") },
            { label: "Page", value: window.location.href },
          ],
        });
        const thankYouName = root.querySelector("#ty-name");
        if (thankYouName) thankYouName.textContent = (name || "friend").split(" ")[0];
        form.reset();
        if (formContainer) formContainer.style.display = "none";
        thankYou?.classList.add("active");
        openModal(leadModal);
      };
      form.addEventListener("submit", handler);
      cleanups.push(() => form.removeEventListener("submit", handler));
    }

    const mapFrame = root.querySelector<HTMLElement>("#map-frame");
    if (mapFrame && "IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          const src = mapFrame.dataset.mapSrc;
          if (src && !mapFrame.querySelector("iframe")) {
            const iframe = document.createElement("iframe");
            iframe.src = src;
            iframe.width = "100%";
            iframe.height = "100%";
            iframe.loading = "lazy";
            iframe.style.border = "0";
            iframe.title = `Healic Hospital ${city} — map`;
            iframe.referrerPolicy = "no-referrer-when-downgrade";
            mapFrame.appendChild(iframe);
          }
          observer.disconnect();
        },
        { rootMargin: "300px" },
      );
      observer.observe(mapFrame);
      cleanups.push(() => observer.disconnect());
    }

    return () => {
      cleanups.forEach((cleanup) => cleanup());
      document.documentElement.classList.remove("modal-open");
    };
  }, [pageType, city]);

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: PILES_GURGAON_CSS + LP_CHROME_CSS }} />
      <div className="lp-chrome"><SiteHeader /></div>
      <div ref={rootRef} className="lp-piles-gurgaon" data-cta-mode={ctaMode} dangerouslySetInnerHTML={{ __html: finalHtml }} />
      <div className="lp-chrome"><SiteFooter /></div>
    </>
  );
}